import { drivelabels_v2 } from '@googleapis/drivelabels';

interface GoogleTypeColorLike {
  red?: number | null;
  green?: number | null;
  blue?: number | null;
  alpha?: number | null;
}

function googleColorToCss(
  color?: GoogleTypeColorLike | null
): string | undefined {
  if (!color) {
    return undefined;
  }

  const r = Math.round((color.red ?? 0) * 255);
  const g = Math.round((color.green ?? 0) * 255);
  const b = Math.round((color.blue ?? 0) * 255);
  const a = color.alpha ?? 1;
  return `rgba(${r}, ${g}, ${b}, ${a})`;
}

export interface ChoiceBadgeColors {
  backgroundColor: string;
  foregroundColor: string;
}

export function getBadgeColorsForChoice(
  choice: drivelabels_v2.Schema$GoogleAppsDriveLabelsV2FieldSelectionOptionsChoice,
  isDarkMode: boolean
): ChoiceBadgeColors | undefined {
  const badgeColors = isDarkMode
    ? choice.displayHints?.darkBadgeColors
    : choice.displayHints?.badgeColors;
  const backgroundColor = googleColorToCss(badgeColors?.backgroundColor);
  const foregroundColor = googleColorToCss(badgeColors?.foregroundColor);
  if (!backgroundColor || !foregroundColor) {
    return undefined;
  }
  return { backgroundColor, foregroundColor };
}

export function getChoiceBadgeColors(
  label: drivelabels_v2.Schema$GoogleAppsDriveLabelsV2Label,
  choiceId: string,
  isDarkMode: boolean
): ChoiceBadgeColors | undefined {
  for (const field of label.fields ?? []) {
    for (const choice of field.selectionOptions?.choices ?? []) {
      if (choice.id === choiceId) {
        return getBadgeColorsForChoice(choice, isDarkMode);
      }
    }
  }
  return undefined;
}

export interface ChoiceBadgeInfo extends ChoiceBadgeColors {
  id: string;
  text: string;
  disabled: boolean;
}

export function getBadgeInfoForChoice(
  choice: drivelabels_v2.Schema$GoogleAppsDriveLabelsV2FieldSelectionOptionsChoice,
  isDarkMode: boolean
): ChoiceBadgeInfo | undefined {
  const colors = getBadgeColorsForChoice(choice, isDarkMode);
  if (!colors) {
    return undefined;
  }
  return {
    id: choice.id || '',
    text: choice.properties?.displayName || choice.id || '',
    disabled: choice.lifecycle?.state === 'DISABLED',
    ...colors,
  };
}

export function getChoiceBadgeInfo(
  label: drivelabels_v2.Schema$GoogleAppsDriveLabelsV2Label,
  choiceId: string,
  isDarkMode: boolean
): ChoiceBadgeInfo | undefined {
  for (const field of label.fields ?? []) {
    for (const choice of field.selectionOptions?.choices ?? []) {
      if (choice.id === choiceId) {
        return getBadgeInfoForChoice(choice, isDarkMode);
      }
    }
  }
  return undefined;
}

function isHiddenFromApply(
  lifecycle: drivelabels_v2.Schema$GoogleAppsDriveLabelsV2Lifecycle | undefined
): boolean {
  if (lifecycle?.state !== 'DISABLED') {
    return false;
  }
  return lifecycle.disabledPolicy?.showInApply === false;
}

export function getAllLabelBadges(
  label: drivelabels_v2.Schema$GoogleAppsDriveLabelsV2Label,
  isDarkMode: boolean
): ChoiceBadgeInfo[] {
  const badges: ChoiceBadgeInfo[] = [];
  for (const field of label.fields ?? []) {
    if (isHiddenFromApply(field.lifecycle)) {
      continue;
    }
    const fieldDisabled = field.lifecycle?.state === 'DISABLED';

    for (const choice of field.selectionOptions?.choices ?? []) {
      if (isHiddenFromApply(choice.lifecycle)) {
        continue;
      }
      const badge = getBadgeInfoForChoice(choice, isDarkMode);
      if (badge) {
        badges.push(
          fieldDisabled && !badge.disabled
            ? { ...badge, disabled: true }
            : badge
        );
      }
    }
  }
  return badges;
}
